import { Type } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBan,
  faChartSimpleHorizontal,
  faCheckCircle,
  faEyeSlash,
  faGaugeMax,
  faImage,
  faInputPipe,
  faInputText,
  faListCheck,
  faListDropdown,
  faMemo,
  faRankingStar,
  faRectangleHistoryCirclePlus,
  faRectangleVerticalHistory,
  faSparkles,
  faStar,
  faUpload
} from '@fortawesome/pro-regular-svg-icons';
import { QuestionType } from '@empathy/survey/base';

import { AIChatDescriptionComponent } from '../components/question-type-description-components/ai-chat/ai-chat-description.component';
import { CarouselDescriptionComponent } from '../components/question-type-description-components/carousel/carousel-description.component';
import { CarouselRatingDescriptionComponent } from '../components/question-type-description-components/carousel-rating/carousel-rating-description.component';
import { DemographicsDescriptionComponent } from '../components/question-type-description-components/demographics/demographics-description.component';
import { DropdownDescriptionComponent } from '../components/question-type-description-components/dropdown/dropdown-description.component';
import { EssayDescriptionComponent } from '../components/question-type-description-components/essay/essay-description.component';
import { FileUploadDescriptionComponent } from '../components/question-type-description-components/file-upload/file-upload-description.component';
import { HeatmapDescriptionComponent } from '../components/question-type-description-components/heatmap/heatmap-description.component';
import { InfoPageDescriptionComponent } from '../components/question-type-description-components/info-page/info-page-description.component';
import { InputDescriptionComponent } from '../components/question-type-description-components/input/input-description.component';
import { MultiChoiceDescriptionComponent } from '../components/question-type-description-components/multi-choice/multi-choice-description.component';
import { NPSDescriptionComponent } from '../components/question-type-description-components/nps/nps-description.component';
import { RankSortDescriptionComponent } from '../components/question-type-description-components/rank-sort/rank-sort-description.component';
import { RankingDescriptionComponent } from '../components/question-type-description-components/ranking/ranking-description.component';
import { RatingDescriptionComponent } from '../components/question-type-description-components/rating/rating-description.component';
import { SingleChoiceDescriptionComponent } from '../components/question-type-description-components/single-choice/single-choice-description.component';
import { TerminateDescriptionComponent } from '../components/question-type-description-components/terminate/terminate-description.component';

export type DemographicsGroup = 'demographics';

export interface IQuestionTypeConfig {
  active: boolean;
  type: QuestionType | DemographicsGroup;
  icon: IconDefinition;
  text: string;
  subText: string;
  descriptionComponent: Type<any>;
  thumbnail: string;
  video: string;
}

export const QuestionTypeConfig: Record<QuestionType | DemographicsGroup, IQuestionTypeConfig> = {
  [QuestionType.SingleChoice]: {
    active: true,
    type: QuestionType.SingleChoice,
    icon: faCheckCircle,
    text: 'Single Choice',
    subText: 'Radio Button Group',
    descriptionComponent: SingleChoiceDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/4b92b439-a72e-4310-d1e2-6744cbed6a00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/df7a1850ed9b977d6fbc4bffeaa92679/manifest/video.m3u8'
  },
  [QuestionType.MultiChoice]: {
    active: true,
    type: QuestionType.MultiChoice,
    icon: faListCheck,
    text: 'Multi Choice',
    subText: 'Checkbox List',
    descriptionComponent: MultiChoiceDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/420848e8-26de-44cd-04f3-67eb09d90100/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/a4da42101510d6492ae4a1fd27297866/manifest/video.m3u8'
  },
  [QuestionType.Essay]: {
    active: true,
    type: QuestionType.Essay,
    icon: faInputText,
    text: 'Open Ended Question',
    subText: '',
    descriptionComponent: EssayDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/7b5b2e68-7f66-4075-681e-99b19947aa00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/134457fcb73eea2d200bba1af7ea8471/manifest/video.m3u8'
  },
  [QuestionType.Input]: {
    active: true,
    type: QuestionType.Input,
    icon: faInputPipe,
    text: 'Free Text',
    subText: '',
    descriptionComponent: InputDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/c63505b2-e92a-45e0-1234-2218940fb000/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/de0fba3559b8ef0e56df51101124a809/manifest/video.m3u8'
  },
  [QuestionType.AIChat]: {
    active: true,
    type: QuestionType.AIChat,
    icon: faSparkles,
    text: 'AI Chat',
    subText: 'AI generated prompts',
    descriptionComponent: AIChatDescriptionComponent,
    thumbnail: undefined,
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/05eb9d18a60b047a7938810ff7e4b426/manifest/video.m3u8'
  },
  [QuestionType.Carousel]: {
    active: true,
    type: QuestionType.Carousel,
    icon: faRectangleVerticalHistory,
    text: 'Carousel',
    subText: '',
    descriptionComponent: CarouselDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/375d9f0a-eb2b-43a0-0e6a-1b8fa1d8d800/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/8f9d6e228e3509b2fcea6f98f2f52239/manifest/video.m3u8'
  },
  [QuestionType.CarouselMultiChoice]: {
    active: true,
    type: QuestionType.CarouselMultiChoice,
    icon: faRectangleVerticalHistory,
    text: 'Carousel Multi Choice',
    subText: '',
    descriptionComponent: CarouselDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/375d9f0a-eb2b-43a0-0e6a-1b8fa1d8d800/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/7ec573c4ef6fe0f3f74771c98fe5c70b/manifest/video.m3u8'
  },
  [QuestionType.CarouselRating]: {
    active: true,
    type: QuestionType.CarouselRating,
    icon: faRectangleVerticalHistory,
    text: 'Carousel Rating',
    subText: '',
    descriptionComponent: CarouselRatingDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/92901010-b86e-4904-b8ac-f9055718da00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/4e4427d9081573ba858f8e24f04e19f2/manifest/video.m3u8'
  },
  [QuestionType.Rating]: {
    active: true,
    type: QuestionType.Rating,
    icon: faStar,
    text: 'Rating',
    subText: '',
    descriptionComponent: RatingDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/1687138f-3b1a-4763-8a50-0644792dde00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/2c141f7aeda94612353285b511a7a204/manifest/video.m3u8'
  },
  [QuestionType.FileUpload]: {
    active: true,
    type: QuestionType.FileUpload,
    icon: faUpload,
    text: 'File Upload',
    subText: '',
    descriptionComponent: FileUploadDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/7e1d2df9-24ea-4a95-1963-0eaeca563600/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/e64f32e933dc3360ce42d26b8bfa5675/manifest/video.m3u8'
  },
  [QuestionType.InfoPage]: {
    active: true,
    type: QuestionType.InfoPage,
    icon: faMemo,
    text: 'Info Page',
    subText: 'Readonly information',
    descriptionComponent: InfoPageDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/c63505b2-e92a-45e0-1234-2218940fb000/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/1f4a192423014bf3202c1f8626f78f83/manifest/video.m3u8'
  },
  [QuestionType.Terminate]: {
    active: true,
    type: QuestionType.Terminate,
    icon: faBan,
    text: 'Terminate',
    subText: '',
    descriptionComponent: TerminateDescriptionComponent,
    thumbnail: undefined,
    video: undefined
  },
  [QuestionType.NPS]: {
    active: true,
    type: QuestionType.NPS,
    icon: faGaugeMax,
    text: 'NPS',
    subText: 'Net Promoter Score',
    descriptionComponent: NPSDescriptionComponent,
    thumbnail: undefined,
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/912557a3db2e213be1e09dfd25d4efc4/manifest/video.m3u8'
  },
  [QuestionType.Ranking]: {
    active: true,
    type: QuestionType.Ranking,
    icon: faChartSimpleHorizontal,
    text: 'Ranking Question',
    subText: '',
    descriptionComponent: RankingDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/6aaff75a-c3d5-44bb-6153-54eb1cf91f00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/8934d146ccfbf2fb29b70f01722fcada/manifest/video.m3u8'
  },
  [QuestionType.Dropdown]: {
    active: true,
    type: QuestionType.Dropdown,
    icon: faListDropdown,
    text: 'Dropdown',
    subText: '',
    descriptionComponent: DropdownDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/c372de6d-6b88-4dd0-10ab-aa27a4e03d00/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/eb320c5fd5933aad519572d2a230706e/manifest/video.m3u8'
  },
  [QuestionType.Heatmap]: {
    active: true,
    type: QuestionType.Heatmap,
    icon: faImage,
    text: 'Heatmap',
    subText: '',
    descriptionComponent: HeatmapDescriptionComponent,
    thumbnail: undefined, // 'https://imagedelivery.net/ED1cNQpdUx5kbjCQ9bj2aQ/3c60dc17-9829-4c68-9d64-6c99558fc600/public',
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/921aa6eb422b28a15cf99ea2212b1e98/manifest/video.m3u8'
  },
  [QuestionType.HiddenChoice]: {
    active: false,
    type: QuestionType.HiddenChoice,
    icon: faEyeSlash,
    text: 'Hidden Choice',
    subText: 'Hidden Variables',
    descriptionComponent: undefined,
    thumbnail: undefined,
    video: undefined
  },
  [QuestionType.RankSort]: {
    active: true,
    type: QuestionType.RankSort,
    icon: faRankingStar,
    text: 'Rank Sort',
    subText: '',
    descriptionComponent: RankSortDescriptionComponent,
    thumbnail: undefined,
    video: 'https://customer-2tclz1ju6c1o3s2g.cloudflarestream.com/1b994c2bb5eed786e11e9c65019e9c69/manifest/video.m3u8'
  },
  demographics: {
    active: true,
    type: 'demographics',
    icon: faRectangleHistoryCirclePlus,
    text: 'Demographics',
    subText: 'Demographic questions',
    descriptionComponent: DemographicsDescriptionComponent,
    thumbnail: undefined,
    video: undefined
  }
};
